/**
 * Vesper Background Upload Store (Zustand)
 *
 * Tracks media upload progress across navigation. The upload continues
 * in the background via the post form's fire-and-forget flow, while
 * this store provides UI state for the floating UploadIndicator.
 */

import { create } from 'zustand';

const MAX_RETRIES = 3;

export interface ActiveUpload {
  id: string;
  progress: number; // 0-100
  status: 'compressing' | 'uploading' | 'complete' | 'error';
  error?: string;
  retryCount: number;
}

interface UploadState {
  activeUpload: ActiveUpload | null;
  uploadFn: (() => Promise<void>) | null;
  startUpload: (id: string) => void;
  updateProgress: (progress: number) => void;
  setStatus: (status: ActiveUpload['status'], error?: string) => void;
  clearUpload: () => void;
  setUploadFn: (fn: () => Promise<void>) => void;
  retryUpload: () => Promise<void>;
}

export const useUploadStore = create<UploadState>((set, get) => ({
  activeUpload: null,
  uploadFn: null,

  startUpload: (id: string) =>
    set({
      activeUpload: { id, progress: 0, status: 'compressing', retryCount: 0 },
    }),

  updateProgress: (progress: number) =>
    set((state) => {
      if (!state.activeUpload) return state;
      return { activeUpload: { ...state.activeUpload, progress } };
    }),

  setStatus: (status: ActiveUpload['status'], error?: string) =>
    set((state) => {
      if (!state.activeUpload) return state;
      return {
        activeUpload: { ...state.activeUpload, status, ...(error ? { error } : {}) },
      };
    }),

  clearUpload: () => set({ activeUpload: null, uploadFn: null }),

  setUploadFn: (fn: () => Promise<void>) => set({ uploadFn: fn }),

  retryUpload: async () => {
    const { activeUpload, uploadFn } = get();
    if (!activeUpload || !uploadFn) return;
    if (activeUpload.retryCount >= MAX_RETRIES) return;

    const newRetryCount = activeUpload.retryCount + 1;
    set({
      activeUpload: {
        ...activeUpload,
        retryCount: newRetryCount,
        status: 'uploading',
        progress: 0,
        error: undefined,
      },
    });

    // backgroundUpload handles its own status updates (complete/error)
    // via the store actions, so we just need to re-invoke it.
    // The try/catch is a safety net for unexpected throws.
    try {
      await uploadFn();
    } catch (err) {
      set((state) => {
        if (!state.activeUpload) return state;
        return {
          activeUpload: {
            ...state.activeUpload,
            status: 'error' as const,
            error: err instanceof Error ? err.message : 'Upload failed',
          },
        };
      });
    }
  },
}));
